import React from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useUserRole } from '../hooks/useUserRoleSecure';
import LoadingSpinner from './LoadingSpinner';
import ErrorMessage from './ErrorMessage';
import { Shield, AlertTriangle } from 'lucide-react';
import './AdminProtectedRoute.css';

/**
 * AdminProtectedRoute component
 * Protects admin-only routes by checking user authentication and admin role
 */
const AdminProtectedRoute = ({ children }) => {
  const { user, loading: authLoading } = useAuth();
  const { userRole, loading: roleLoading, error, canAccessAdmin, refetch } = useUserRole();

  // Show loading while checking authentication and role
  if (authLoading || roleLoading) {
    return <LoadingSpinner message="Checking admin permissions..." />;
  }

  // Handle errors in role fetching
  if (error) {
    return (
      <ErrorMessage 
        message={`Error checking admin permissions: ${error}`}
        onRetry={refetch}
        onDismiss={() => {}}
      />
    );
  }

  // User not authenticated
  if (!user) {
    return (
      <div className="admin-access-denied">
        <div className="access-denied-content">
          <Shield className="access-denied-icon" size={64} />
          <h2>Authentication Required</h2>
          <p>You must be signed in to access the admin panel.</p>
          <button 
            className="retry-button"
            onClick={() => window.location.reload()}
          >
            Sign In
          </button>
        </div>
      </div>
    );
  }

  // User doesn't have admin access
  if (!canAccessAdmin) {
    return (
      <div className="admin-access-denied">
        <div className="access-denied-content">
          <AlertTriangle className="access-denied-icon warning" size={64} />
          <h2>Access Denied</h2>
          <p>
            You don't have permission to access the admin panel. 
            Current role: <span className="current-role">{userRole || 'member'}</span>
          </p>
          <p className="access-note">
            Contact your administrator if you believe you should have admin access.
          </p>
          <button 
            className="back-button"
            onClick={() => window.history.back()}
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  // User has admin access - render protected content
  return children;
};

export default AdminProtectedRoute;